/**
 * In-app Resources & Support content.
 * Curated from humblytics-lp `faq-data.ts`, /resources, and `learning-resources.json`.
 */

type SupportLink = {
  label: string
  description: string
  url: string
}

type SupportFaq = {
  q: string
  a: string
}

type SupportFaqCategory = {
  id: string
  label: string
  items: SupportFaq[]
}

export const SETUP_LINKS: SupportLink[] = [
  {
    label: 'Getting started',
    description: 'Install the script and verify tracking',
    url: 'https://docs.humblytics.com/how-to-get-started',
  },
  {
    label: 'Verify tracking',
    description: 'Troubleshoot when data is not showing',
    url: 'https://docs.humblytics.com/how-to-get-started/verify-and-troubleshoot-tracking',
  },
  {
    label: 'Understanding your data',
    description: 'Views, visits, visitors and more',
    url: 'https://docs.humblytics.com/understanding-your-data',
  },
  {
    label: 'Forms data explained',
    description: 'Submissions vs unique sessions',
    url: 'https://docs.humblytics.com/understanding-your-data',
  },
]

export const GUIDE_LINKS: SupportLink[] = [
  {
    label: 'How split testing works',
    description: 'Under the hood of no-code A/B tests',
    url: 'https://docs.humblytics.com/split-testing-overview/how-humblytics-split-testing-works-under-the-hood',
  },
  {
    label: 'Set up a split test',
    description: 'Step-by-step first experiment',
    url: 'https://docs.humblytics.com/split-testing-overview/how-to-setup-a-split-test',
  },
  {
    label: 'Analyze test results',
    description: 'Significance, confidence and winners',
    url: 'https://docs.humblytics.com/split-testing-overview/how-to-analyze-split-test-data',
  },
  {
    label: 'Funnel analysis guide',
    description: 'Find drop-offs and fix the journey',
    url: 'https://humblytics.com/blog/the-complete-guide-to-funnel-analysis-in-humblytics',
  },
  {
    label: 'Heatmaps for conversions',
    description: 'Click and scroll maps that drive CRO',
    url: 'https://humblytics.com/blog/how-to-use-heatmaps-to-improve-website-conversions',
  },
  {
    label: 'Stripe revenue attribution',
    description: 'Connect purchases to traffic sources',
    url: 'https://docs.humblytics.com/how-to-track-purchase-events/stripe',
  },
  {
    label: 'UTM campaign tracking',
    description: 'Name and track paid campaigns',
    url: 'https://docs.humblytics.com/campaign-tracking-with-utm-links',
  },
  {
    label: 'Cross-domain tracking',
    description: 'Follow journeys across domains',
    url: 'https://docs.humblytics.com/cross-domain-tracking-and-whitelisting',
  },
]

export const EXTERNAL_RESOURCES: SupportLink[] = [
  {
    label: 'Documentation',
    description: 'Full product docs and API reference',
    url: 'https://docs.humblytics.com',
  },
  {
    label: 'Video tutorials',
    description: 'Walkthroughs on YouTube',
    url: 'https://www.youtube.com/@humblytics',
  },
  {
    label: 'Revenue Lab',
    description: 'CRO guides and frameworks on the blog',
    url: 'https://humblytics.com/guides',
  },
  {
    label: 'Public FAQ',
    description: 'Answers to common questions',
    url: 'https://humblytics.com/resources',
  },
]

export const FAQ_CATEGORIES: SupportFaqCategory[] = [
  {
    id: 'start',
    label: 'Getting started',
    items: [
      {
        q: 'How do I install Humblytics?',
        a: 'Copy your tracking script from Site settings → Install and add it to your site’s <head>. Webflow and Framer have platform-specific guides. The script loads asynchronously (~16KB) and won’t block rendering.',
      },
      {
        q: 'Do I need developers to set this up?',
        a: 'No. Add one script tag and you’re tracking. The visual editor lets you run element A/B tests without touching code. Launch experiments in minutes, not sprints.',
      },
      {
        q: 'How long until I see data?',
        a: 'Page views, clicks, and form events appear within seconds once traffic hits a page with the script. Heatmaps update in real time; A/B results refresh every few minutes.',
      },
      {
        q: 'Verify says not installed, but I see traffic. Why?',
        a: 'Verification can false-negative (ad blockers, cached pages, SPA timing). If your dashboard already shows sessions, tracking is working. Use Site settings → Install to re-check, or chat with us if a specific page is blank.',
      },
    ],
  },
  {
    id: 'data',
    label: 'Tracking and data',
    items: [
      {
        q: 'Why does my form conversion look over 100%?',
        a: 'The dashboard Submissions number counts every form submit event, not unique people. One visitor submitting twice counts as two. Unique sessions (distinct visits that submitted) is the honest conversion denominator. Use that when reconciling against a CRM.',
      },
      {
        q: 'Why are my clicks higher than my visitors?',
        a: 'Clicks count every click event; one visitor can click many times. CTR can go above 100% (e.g. 200 sessions → 500 clicks = 250% CTR). Expected behavior, not a bug. GA4 defines users/events differently, so numbers won’t match 1:1.',
      },
      {
        q: 'What’s the difference between views, visits, and visitors?',
        a: 'Views = pageviews. Visits = unique sessions (~30m). Visitors = unique people. Funnels always report visits. Metric definitions live under Understanding your data in the docs.',
      },
      {
        q: 'Can I track forms from Typeform, HubSpot, GHL, etc.?',
        a: 'Yes. Native HTML forms plus Typeform, Tally, Cal.com, HubSpot Forms & Booking, Fillout, GoHighLevel, and more. See Connectors for setup. Embedded HubSpot forms are auto-detected when the script is on the page.',
      },
      {
        q: 'Will the script slow down my site?',
        a: 'No. About 16KB, async, CDN-delivered. No noticeable impact on page load or Core Web Vitals, including mobile.',
      },
    ],
  },
  {
    id: 'experiments',
    label: 'Experiments',
    items: [
      {
        q: 'How do A/B tests work?',
        a: 'Create no-code tests that compare page or element variants. Humblytics splits traffic, tracks your goal, and shows confidence / significance. Element tests apply overrides at runtime. No deploy needed on most platforms.',
      },
      {
        q: 'How do I choose a conversion goal?',
        a: 'Pick what you’re optimizing for: form submissions, click-through rate, destination page, external destination, revenue, session time, or bounce rate. Goal choice drives how the verdict card reads a winner.',
      },
      {
        q: 'How long should I run a test?',
        a: 'Use the Test Planner for sample size and duration from your traffic, baseline rate, and minimum detectable effect. Stopping early risks false positives; running forever wastes opportunity. Ship when confidence clears your threshold.',
      },
      {
        q: 'My traffic split looks uneven. Why?',
        a: 'Often a variant links back to the control URL (e.g. #start anchors). Visitors leave the variant and land on control, skewing the split. Prefer relative fragments or variant-specific paths. Humblytics also flags sample-ratio mismatch (SRM) on the experiment detail.',
      },
      {
        q: 'Can I run multiple tests at once?',
        a: 'Yes on supported plans. Avoid overlapping elements on the same page, since that muddies attribution. Each experiment tracks independently.',
      },
    ],
  },
  {
    id: 'account',
    label: 'Privacy and account',
    items: [
      {
        q: 'Is Humblytics GDPR compliant? Do I need a cookie banner?',
        a: 'Yes. Cookieless analytics with hashed identifiers. Consent banners aren’t required for Humblytics tracking in most jurisdictions; confirm with your counsel for your stack.',
      },
      {
        q: 'What happens if I exceed my event limit?',
        a: 'Email alert at ~75% usage, then a short grace period. After that tracking may pause until you upgrade, add an events add-on, or the monthly limit resets (1st of each calendar month).',
      },
      {
        q: 'Can I add team members?',
        a: 'Team seats ship on Scale (5) and Enterprise (unlimited). Plus/Business are owner-only. Members can be scoped to sites inside a workspace.',
      },
      {
        q: 'How do I export data or share a dashboard?',
        a: 'CSV export from Export; API access on all plans. Share links create public dashboard URLs with expiry. Create them from the site/workspace share flow.',
      },
    ],
  },
]
